import { gettext } from '@eventbrite/i18n';
import { css } from 'emotion';
import React from 'react';
import Divider from '../../divider/Divider';
import Icon from '../../icon/Icon';
import LogoWordmarkBrandSvg from '../../iconography/icons/LogoWordmarkBrand';
import Modal from '../../modal';
import Select from '../../select/Select';
import { useTheme } from '../../theme-context';

const EVENTBRITE_LOGO_COLOR = '#A9A8B3';

const PoweredByEventbriteText = () => {
    return (
        <a
            target="_blank"
            rel="noopener noreferrer"
            className="checkout-widget__powered-by-eventbrite eds-text-color--grey-600 eds-text-bs"
            title={gettext('Powered by Eventbrite').toString()}
            href="http://www.eventbrite.com"
        >
            {gettext('Powered by')}
            <Icon
                classes={css({
                    width: '62px',
                    height: '62px',
                    display: 'inline-block',
                    marginLeft: '4px',
                })}
                type={<LogoWordmarkBrandSvg fill={EVENTBRITE_LOGO_COLOR} />}
            />
        </a>
    );
};

interface IPaymentInfo {
    handleSelector: (_: any) => void;
    onBlur: () => void;
    paymentOptions: {
        value: string;
        display: string;
    }[];
    selectedValue?: string;
}

const PaymentMethod = ({
    handleSelector,
    onBlur,
    paymentOptions,
    selectedValue,
}: IPaymentInfo): React.ReactElement => {
    const { getFont, styles } = useTheme();

    return (
        <div data-testid="payment-method">
            <h2
                className={css({
                    fontFamily: getFont('desktop/body/large').fontFamily,
                    fontWeight: getFont('desktop/body/large').fontWeight,
                    fontSize: getFont('desktop/body/large').fontSize,
                    color: styles.colors['grey-07'].color,
                    lineHeight: `${
                        getFont('desktop/body/large').lineHeightPx
                    }px`,
                    marginBottom: '16px',
                })}
            >
                {gettext('Payment method')}
            </h2>
            <div className={css({ maxWidth: '329px' })}>
                <Select
                    placeholder={gettext('Enter a value').toString()}
                    value={selectedValue || ''}
                    values={paymentOptions}
                    onBlur={onBlur}
                    onChange={handleSelector}
                />
            </div>
        </div>
    );
};

interface IFooterPrice {
    priceInfo: {
        currency: string;
        price: string;
    };
}

const FooterPrice = ({ priceInfo }: IFooterPrice): React.ReactElement => {
    const { currency, price } = priceInfo;
    const { getFont } = useTheme();

    return (
        <div
            className={css({
                display: 'grid',
                gridTemplateColumns: 'repeat(2, auto);',
            })}
        >
            <div
                data-testid="currency"
                className={css({
                    fontWeight: getFont('desktop/body/medium').fontWeight,
                    fontSize: getFont('desktop/body/medium').fontSize,
                    lineHeight: `${
                        getFont('desktop/body/medium').lineHeightPx
                    }px`,
                    marginRight: '4px',
                })}
            >
                {currency}
            </div>
            <div
                data-testid="price"
                className={css({
                    fontWeight: getFont('desktop/body/medium/bold').fontWeight,
                    fontSize: getFont('desktop/body/medium/bold').fontSize,
                    lineHeight: `${
                        getFont('desktop/body/medium/bold').lineHeightPx
                    }px`,
                })}
            >
                {price}
            </div>
        </div>
    );
};

const ManualOrderDisclaimer = (): React.ReactElement => {
    const { getFont, styles } = useTheme();

    return (
        <div data-testid="manual-order-disclaimer">
            <section
                className={css({
                    fontFamily: getFont('desktop/body/small').fontFamily,
                    fontWeight: getFont('desktop/body/small').fontWeight,
                    fontSize: getFont('desktop/body/small').fontSize,
                    color: styles.colors['grey-07'].color,
                    lineHeight: `${
                        getFont('desktop/body/small').lineHeightPx
                    }px`,
                    margin: '34px 0 16px',
                })}
            >
                {gettext(
                    'Eventbrite does not charge any fees for manual orders',
                )}
            </section>
            <section
                className={css({
                    fontFamily: getFont('desktop/body/small').fontFamily,
                    fontWeight: getFont('desktop/body/small').fontWeight,
                    fontSize: getFont('desktop/body/small').fontSize,
                    color: styles.colors['grey-07'].color,
                    lineHeight: `${
                        getFont('desktop/body/small').lineHeightPx
                    }px`,
                    marginBottom: '16px',
                })}
            >
                {gettext(
                    'As a reminder, the creator is responsible for compliance with privacy and marketing regulation when using this feature to upload email addresses for marketing communications.',
                )}
            </section>
            <PoweredByEventbriteText />
        </div>
    );
};

interface IModalCheckout {
    priceInfo: {
        currency: string;
        price: string;
    };
    paymentInfo: IPaymentInfo;
    handleClose: () => void;
    onPrimaryAction: () => void;
    disablePrimaryAction?: boolean;
}

const ModalCheckout = ({
    priceInfo,
    paymentInfo,
    handleClose,
    onPrimaryAction,
    disablePrimaryAction,
}: IModalCheckout): React.ReactElement => {
    const footerContent = <FooterPrice priceInfo={priceInfo} />;

    return (
        <Modal
            isShown={true}
            title={gettext('Checkout')}
            onClose={handleClose}
            showCloseButton={true}
            height={460}
            width={720}
            onPrimaryAction={onPrimaryAction}
            primaryActionLabel={gettext('Next')}
            footerContent={footerContent}
            disablePrimaryAction={disablePrimaryAction}
        >
            <div
                className={css({
                    padding: '34px 81px 0px 80px',
                })}
            >
                <PaymentMethod {...paymentInfo} />
                <div
                    className={css({
                        margin: '32px 0px',
                    })}
                >
                    <Divider />
                </div>
                <ManualOrderDisclaimer />
            </div>
        </Modal>
    );
};

export default ModalCheckout;
